import { BigNumber } from "bignumber.js";
import { cn, delay, PropsWithClassName } from "kz-ui-sdk";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import styles from "./SpinningNumber.module.css";
import toSpinningDigits from "./utils";
import { ANIMATION_DEFAULTS, BOUNCE_CURVES, DIGITS } from "./utils";

export interface SpinningNumberRef {
  startSpin: (value?: number | BigNumber) => void;
  stopSpin?: () => void;
  hideSpin?: () => void;
}

interface SpinningNumberProps extends PropsWithClassName {
  initialValue?: number | BigNumber;
  autoStart?: boolean;
  animate?: {
    spinDuration?: number;
    stopDelay?: number;
    spinningHideMs?: number;
  };
  style?: {
    containerHeight?: number;
    containerWidth?: number;
    digitWidth?: number;
    className?: string;
    digitClassName?: string;
    wheelClassName?: string;
    zIndex?: number;
    font?: {
      size?: number;
      weight?: number;
      letterSpacing?: string;
    };
  };
  onComplete?: () => void;
}

const SpinningNumber = forwardRef<SpinningNumberRef, SpinningNumberProps>(
  ({ initialValue, autoStart = false, animate = {}, style = {}, className, onComplete }, ref) => {
    const { size: fontSize = 46, weight: fontWeight = "bold", letterSpacing = "-2.3px" } = style.font || {};
    const { spinDuration = ANIMATION_DEFAULTS.spinDuration, stopDelay = ANIMATION_DEFAULTS.stopDelay, spinningHideMs = ANIMATION_DEFAULTS.spinningHideMs } = animate;
    const {
      containerHeight = 120,
      containerWidth = "auto",
      digitWidth = 29,
      digitClassName = "",
      wheelClassName = "",
      zIndex,
    } = style;

    const containerHeightNum = typeof containerHeight === "string" ? parseFloat(containerHeight) : containerHeight;
    const digitHeight = containerHeightNum / 2.6;
    const wheelOffset = digitHeight * 2.8;
    const digitWidthNum = typeof digitWidth === "string" ? parseFloat(digitWidth) : digitWidth;
    const punctuationMargin = digitWidthNum * -0.3;
    const [isHighlighted, setIsHighlighted] = useState(false);
    const [currentValue, setCurrentValue] = useState<number | BigNumber | undefined>(initialValue);
    const [isAnimating, setIsAnimating] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const layoutNumberRef = useRef<HTMLDivElement | null>(null);
    const [targetValue, setTargetValue] = useState<number | BigNumber | undefined>(undefined);
    const animations = useRef<{ spin: () => void; stop: (delay: number) => void }[]>([]);
    const lastDigitCompleted = useRef(false);
    const displayValue = currentValue !== undefined ? currentValue : initialValue !== undefined ? initialValue : 0;
    const valueForFormatting = isAnimating && targetValue !== undefined ? targetValue : displayValue;

    const formattedDisplayValue = useMemo(() => {
      const formatted = toSpinningDigits(
        typeof valueForFormatting === "number" ? valueForFormatting : valueForFormatting.toNumber(),
      );
      // Split the K suffix if present
      const match = formatted.match(/^(.+?)K$/);
      return match ? match[1] : formatted;
    }, [valueForFormatting]);

    const hasKSuffix = useMemo(() => {
      const formatted = toSpinningDigits(
        typeof valueForFormatting === "number" ? valueForFormatting : valueForFormatting.toNumber(),
      );
      return formatted.endsWith("K");
    }, [valueForFormatting]);

    // start spinning animation
    const startAnimation = useCallback(
      (ref: HTMLDivElement | null, index: number, value: number | BigNumber | undefined, onComplete?: () => void) => {
        const factor = 5 + Math.floor(Math.random() * 10);
        const section = 100 / DIGITS.length;
        let spinTimer: number;

        // start from a random number
        const randomStart = Math.floor(Math.random() * DIGITS.length);
        if (ref) {
          ref.style.transform = `translateY(-${randomStart * section}%)`;
          ref.style.opacity = "1";
        }

        const spin = () => {
          if (ref) ref.style.transition = "";
          let value = randomStart * section;

          const animate = () => {
            if (value <= -(section * DIGITS.length)) {
              value = 0;
            }
            value -= section / factor;
            if (ref) {
              ref.style.transform = `translateY(${-value}%)`;
              ref.style.opacity = "1";
            }
            spinTimer = requestAnimationFrame(animate);
          };
          spinTimer = requestAnimationFrame(animate);
        };

        // stop spinning animation
        const stopAnimation = (delay: number | undefined) => {
          setTimeout(() => {
            cancelAnimationFrame(spinTimer);
            if (ref && value !== undefined) {
              const formattedValue = toSpinningDigits(typeof value === "number" ? value : value.toNumber());
              const targetNumber = formattedValue[index];
              const targetIndex = DIGITS.length - DIGITS.indexOf(targetNumber);
              const targetPosition = section * targetIndex - section;

              // get random bounce curve and duration
              const bounceCurveKeys = Object.keys(BOUNCE_CURVES) as (keyof typeof BOUNCE_CURVES)[];
              const randomBounceCurve =
                BOUNCE_CURVES[bounceCurveKeys[Math.floor(Math.random() * bounceCurveKeys.length)]];
              const bounceDuration =
                Math.random() * (ANIMATION_DEFAULTS.bounceMaxDuration - ANIMATION_DEFAULTS.bounceMinDuration) +
                ANIMATION_DEFAULTS.bounceMinDuration;

              // reset the position of the wheel
              ref.style.opacity = "0";
              ref.style.transform = `translateY(-${wheelOffset}px)`;
              ref.style.transitionDuration = "0.1s";
              ref.style.transitionProperty = "transform";

              // scroll down to the target position with bounce effect
              setTimeout(() => {
                ref.style.transitionDuration = `${bounceDuration}s`;
                ref.style.transitionTimingFunction = randomBounceCurve;
                ref.style.transform = `translateY(${targetPosition}%)`;
                ref.style.opacity = "1";

                // Call completion callback after bounce animation completes
                setTimeout(() => {
                  if (onComplete) {
                    onComplete();
                  }
                }, bounceDuration * 1000);

                if (targetPosition === -81.81818181818183 && formattedValue[index] === "-") {
                  ref.style.transition = "transform 1.5s ease-out";
                  ref.style.opacity = "0";
                }
              }, 100);
            }
          }, delay);
        };

        return { spin, stop: stopAnimation };
      },
      [wheelOffset],
    );

    // start spinning with value
    const startSpin = useCallback(
      (value?: number | BigNumber) => {
        const targetValue = value !== undefined ? value : currentValue;
        if (targetValue === undefined) return;

        setTargetValue(targetValue);
        setIsVisible(true);
        setIsAnimating(true);
        setIsHighlighted(false);
        lastDigitCompleted.current = false;

        const formattedValue = toSpinningDigits(typeof targetValue === "number" ? targetValue : targetValue.toNumber());
        const amountStrLength = formattedValue.length;

        // defer animation start until DOM updates for new wheels
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            animations.current = Array.from({
              length: amountStrLength,
            }).map((_, index) => {
              const reverseIndex = amountStrLength - index - 1;
              const wheelRef = document.getElementById(
                ["spinning-number-wheel", reverseIndex].join("-"),
              ) as HTMLDivElement | null;

              const onCompleteAnimation =
                reverseIndex === 0
                  ? () => {
                      if (!lastDigitCompleted.current) {
                        lastDigitCompleted.current = true;
                        setIsHighlighted(true);
                        setIsAnimating(false);
                        setCurrentValue(targetValue);
                        setTargetValue(undefined);

                        setTimeout(() => {
                          onComplete?.();
                        }, 500);
                      }
                    }
                  : undefined;

              const animation = startAnimation(wheelRef, reverseIndex, targetValue, onCompleteAnimation);
              animation.spin();

              delay(spinDuration).then(() => animation.stop(index * stopDelay));

              return animation;
            });
          });
        });
      },
      [currentValue, startAnimation, spinDuration, stopDelay, onComplete],
    );

    const hideSpin = useCallback(() => {
      const element = layoutNumberRef.current;
      if (!element) return;
      // prepare transition
      element.style.transition = `transform ${spinningHideMs}ms ease-out, opacity ${spinningHideMs}ms ease-out`;
      element.style.willChange = "transform, opacity";
      // ensure starting point
      element.style.transform = "translateY(0px)";
      element.style.opacity = "1";
      requestAnimationFrame(() => {
        element.style.transform = `translateY(${digitHeight}px)`;
        element.style.opacity = "0";
      });
    }, [digitHeight, spinningHideMs]);

    useImperativeHandle(
      ref,
      () => ({
        startSpin,
        hideSpin,
      }),
      [startSpin, hideSpin],
    );

    // auto start if enabled and initialValue is provided
    useEffect(() => {
      if (autoStart && initialValue !== undefined) {
        setIsVisible(true);
        startSpin(initialValue);
      }
    }, [autoStart, initialValue, startSpin]);

    return (
      <>
        <div
          className={cn(
            "absolute bottom-[-10px] flex items-baseline justify-center overflow-hidden transition-opacity duration-500",
            className,
          )}
          style={{
            height: containerHeight,
            width: containerWidth,
            opacity: isVisible ? 1 : 0,
            zIndex,
          }}
        >
          <div
            className="transition-transform duration-500 will-change-transform"
            style={{
              transform: `translateY(calc(-100% + ${wheelOffset}px))`,
            }}
          >
            <div className="flex items-baseline" ref={layoutNumberRef}>
              <div>
                {Array.from({
                  length: formattedDisplayValue.length,
                }).map((_, wheelIndex) => {
                  const currentChar = formattedDisplayValue[wheelIndex] || "0";
                  const isPunctuation = [".", ","].includes(currentChar);
                  const isEmpty = currentChar === "-" || currentChar === undefined;

                  return (
                    <div
                      className={cn(
                        "inline-block animate-fade-in-3s overflow-hidden transition-all duration-500 will-change-transform",
                        {
                          "!max-w-0": isHighlighted && isEmpty,
                        },
                        wheelClassName,
                      )}
                      style={{
                        maxWidth: digitWidth,
                        marginLeft: isHighlighted && isPunctuation ? `${punctuationMargin}px` : undefined,
                        marginRight: isHighlighted && isPunctuation ? `${punctuationMargin}px` : undefined,
                      }}
                      key={["wheel", wheelIndex].join("-")}
                    >
                      <section>
                        <div
                          className={cn("absolute text-center")}
                          style={{
                            top: 0,
                            position: "relative",
                            width: digitWidth,
                          }}
                          id={["spinning-number-wheel", wheelIndex].join("-")}
                        >
                          {DIGITS.map((num) => (
                            <div
                              className={cn("animate-fade-in will-change-transform", digitClassName)}
                              key={["wheel", wheelIndex, "digit", num].join("-")}
                            >
                              <p
                                className={cn("text-center", {
                                  [styles.digitHighlighted]: isHighlighted,
                                  [styles.digitNormal]: !isHighlighted,
                                })}
                                style={{
                                  fontSize: fontSize,
                                  fontWeight: fontWeight,
                                  letterSpacing: letterSpacing,
                                }}
                              >
                                {num}
                              </p>
                            </div>
                          ))}
                        </div>
                      </section>
                    </div>
                  );
                })}
              </div>
              {hasKSuffix && (
                <div
                  className={cn("inline-block overflow-hidden transition-all duration-500", {
                    "!max-w-0": !isHighlighted,
                  })}
                  style={{
                    maxWidth: digitWidth + 2,
                    transform: "translateY(-9px)",
                  }}
                >
                  <div
                    className={cn("absolute text-center")}
                    style={{
                      top: 0,
                      position: "relative",
                      width: digitWidth + 2,
                    }}
                  >
                    <div className="animate-fade-in will-change-transform">
                      <p
                        className={cn("text-center", {
                          [styles.digitHighlighted]: isHighlighted,
                          [styles.digitNormal]: !isHighlighted,
                          "opacity-0": !isHighlighted,
                          "opacity-100": isHighlighted,
                        })}
                        style={{
                          fontSize: fontSize,
                          fontWeight: fontWeight,
                          letterSpacing: letterSpacing,
                        }}
                      >
                        K
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </>
    );
  },
);

SpinningNumber.displayName = "SpinningNumber";

export default SpinningNumber;
